import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, switchMap } from 'rxjs';
import {
  CampaignMainFeatureConfig,
  CampaignTimelineFeatureConfig,
  EntityTypes,
  FeatureConfigResult,
  FeatureTypes,
  WorkspaceFeatureConfig,
  CampaignAssetListFeatureConfig,
} from '../interfaces/feature-config.interface';
import { ApiService } from './api.service';
import { map, tap, retry, catchError, finalize, shareReplay, distinctUntilChanged } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { User } from '../interfaces';
import { EmitterService } from './emitter.service';
import { AuthTokenUpdatedEvent, BrandUpdatedEvent } from '../models/events';

type FeatureConfigMap = {
  [FeatureTypes.FEA_WORKSPACE_MAIN]: WorkspaceFeatureConfig;
  [FeatureTypes.FEA_CAMPAIGN_MAIN]: CampaignMainFeatureConfig;
  [FeatureTypes.FEA_CAMPAIGN_TIMELINE]: CampaignTimelineFeatureConfig;
  [FeatureTypes.FEA_CAMPAIGN_ASSETLIST]: CampaignAssetListFeatureConfig;
};

export type FeatureConfigs<T extends keyof FeatureConfigMap> = {
  [K in T]: FeatureConfigMap[K] | null;
};

type WorkspaceFeatures = Partial<Record<FeatureTypes, object>>;

@Injectable({
  providedIn: 'root',
})
export class FeatureConfigService {
  private readonly apiService: ApiService = inject(ApiService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly configKey: string = 'features';
  private readonly configScopeKey: string = 'features_scope';
  private isInitialized: boolean = false;
  private pendingRequest: { scope: string; request$: Observable<FeatureConfigResult> } | null = null;
  private readonly featureConfigSubject: BehaviorSubject<FeatureConfigResult | null> =
    new BehaviorSubject<FeatureConfigResult | null>(null);
  private readonly workspaceFeatureCache = new Map<string, Observable<WorkspaceFeatures>>();

  public initializedFeatureConfig(): void {
    if (this.isInitialized) {
      return;
    }
    this.isInitialized = true;

    EmitterService.get(AuthTokenUpdatedEvent).subscribe((token: string) => {
      if (!token) {
        this.clearFeatureConfig();
      } else {
        // Social login loads token before user, so adding extra check to avoid race condition
        this.loadFeatureConfig().subscribe();
      }
    });

    EmitterService.get(BrandUpdatedEvent).subscribe(() => {
      this.loadFeatureConfig().subscribe();
    });

    this.loadFeatureConfig().subscribe();
  }

  private requestFeatureConfig(entityType: EntityTypes, entityId: string): Observable<FeatureConfigResult> {
    const endpoint: string = `/api/feature-configuration/${entityType}/${entityId}`;
    return this.apiService.httpGet(endpoint);
  }

  public loadFeatureConfig(): Observable<FeatureConfigResult> {
    const authUser: User = this.authService.getAuthUser();
    if (authUser) {
      const configValue: string = localStorage.getItem(this.configKey);

      // During 3rd-party/social login the token can be set before the legacy user (and its
      // companyId) is fully hydrated. Skip rather than request ENT_DIVISION/undefined (500);
      // the call re-runs once the user is hydrated with a companyId. No scope can be computed
      // yet either, so trust an existing cache instead of invalidating it mid-hydration.
      if (!authUser.companyId) {
        if (configValue) {
          const parsedConfig: FeatureConfigResult = JSON.parse(configValue) as FeatureConfigResult;
          this.featureConfigSubject.next(parsedConfig);
          return of(parsedConfig);
        }
        return of(null);
      }

      // The cache is scoped to company+brand: a mismatch (brand switch, impersonation, tenant
      // switch) is a miss, otherwise brand-scoped flags keep answering for the previous brand.
      const scope: string = `${authUser.companyId}:${authUser.brandId?.trim() ?? ''}`;
      if (configValue && localStorage.getItem(this.configScopeKey) === scope) {
        const parsedConfig: FeatureConfigResult = JSON.parse(configValue) as FeatureConfigResult;
        this.featureConfigSubject.next(parsedConfig);
        return of(parsedConfig);
      }

      if (this.pendingRequest?.scope === scope) {
        return this.pendingRequest.request$;
      }

      // Check if brandId exists and is not empty (undefined must NOT enter the brand branch,
      // otherwise it requests ENT_BRAND/undefined).
      const source$: Observable<FeatureConfigResult> = authUser.brandId?.trim()
        ? // Make requests sequentially
          this.requestFeatureConfig(EntityTypes.ENT_DIVISION, authUser.companyId).pipe(
            retry({ count: 3, delay: 1000 }),
            switchMap((divisionConfig: FeatureConfigResult) =>
              this.requestFeatureConfig(EntityTypes.ENT_BRAND, authUser.brandId).pipe(
                retry({ count: 3, delay: 1000 }),
                map((brandConfig: FeatureConfigResult) => {
                  const config: FeatureConfigResult = {
                    ...divisionConfig,
                    features: {
                      ...divisionConfig.features,
                      ...brandConfig.features,
                    },
                  };
                  this.storeConfig(config, scope);
                  return config;
                })
              )
            )
          )
        : // If no brandId, use original behavior
          this.requestFeatureConfig(EntityTypes.ENT_DIVISION, authUser.companyId).pipe(
            tap((config: FeatureConfigResult) => this.storeConfig(config, scope))
          );

      const request$: Observable<FeatureConfigResult> = source$.pipe(
        finalize(() => {
          if (this.pendingRequest?.scope === scope) {
            this.pendingRequest = null;
          }
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

      this.pendingRequest = { scope, request$ };
      return request$;
    }
    return of(null);
  }

  private storeConfig(config: FeatureConfigResult, scope: string): void {
    localStorage.setItem(this.configKey, JSON.stringify(config));
    localStorage.setItem(this.configScopeKey, scope);
    this.featureConfigSubject.next(config);
  }

  public clearFeatureConfig(): void {
    localStorage.removeItem(this.configKey);
    localStorage.removeItem(this.configScopeKey);
    this.workspaceFeatureCache.clear();
    this.featureConfigSubject.next(null);
  }

  // A workspace without its own rows gets the backend's zero-UUID default back; only rows
  // owned by this workspace may override division/brand, otherwise the default hits every workspace.
  private workspaceFeatures$(workspaceId: string): Observable<WorkspaceFeatures> {
    const cached = this.workspaceFeatureCache.get(workspaceId);
    if (cached) {
      return cached;
    }
    const request$ = this.requestFeatureConfig(EntityTypes.ENT_WORKSPACE, workspaceId).pipe(
      map((res: FeatureConfigResult) =>
        res?.entityId?.toLowerCase() === workspaceId.toLowerCase() ? (res.features ?? {}) : {}
      ),
      catchError(() => of({})),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.workspaceFeatureCache.set(workspaceId, request$);
    return request$;
  }

  // Division/brand configs with this workspace's ENT_WORKSPACE values merged on top, per feature.
  public getWorkspaceFeatureConfigs<T extends keyof FeatureConfigMap>(
    featureTypes: T[],
    workspaceId: string | null | undefined
  ): Observable<FeatureConfigs<T>> {
    const base$ = this.getFeatureConfigs(featureTypes);
    if (!workspaceId) {
      return base$;
    }
    return combineLatest([base$, this.workspaceFeatures$(workspaceId)]).pipe(
      map(
        ([base, workspace]) =>
          Object.fromEntries(
            featureTypes.map((type: T) => [type, workspace[type] ? { ...base[type], ...workspace[type] } : base[type]])
          ) as FeatureConfigs<T>
      )
    );
  }

  //get a feature config by type
  public getTypedFeatureConfig<T extends keyof FeatureConfigMap>(
    featureType: T
  ): Observable<FeatureConfigMap[T] | null> {
    return this.featureConfigSubject.pipe(
      map((value: FeatureConfigResult) => (value?.features[featureType] as FeatureConfigMap[T]) || null)
    );
  }

  public isFeatureActive$(featureType: keyof FeatureConfigMap): Observable<boolean> {
    return this.getTypedFeatureConfig(featureType).pipe(
      map((config) => !!config?.active),
      distinctUntilChanged()
    );
  }

  public getTypedFeatureConfigSnapshot<T extends keyof FeatureConfigMap>(featureType: T): FeatureConfigMap[T] | null {
    const value = this.featureConfigSubject.value;
    return (value?.features?.[featureType] as FeatureConfigMap[T]) || null;
  }

  //get multiple feature configs by types
  public getFeatureConfigs<T extends keyof FeatureConfigMap>(featureTypes: T[]): Observable<FeatureConfigs<T>> {
    return this.featureConfigSubject.pipe(
      map((value: FeatureConfigResult) => {
        const result: FeatureConfigs<T> = {} as FeatureConfigs<T>;
        featureTypes.forEach((type: T) => {
          result[type] = (value?.features[type] as FeatureConfigMap[T]) || null;
        });
        return result;
      })
    );
  }

  public getInitialRoute(): Observable<string[]> {
    const authUser: User = this.authService.getAuthUser();
    if (!authUser) {
      return of(['/entry', 'login']);
    }

    if (authUser.userPermissionType === 'AGENCY') {
      return of(['/workspaces', 'projects']);
    }

    return this.loadFeatureConfig().pipe(
      map((config: FeatureConfigResult) => {
        const workspaceMain = config?.features?.[FeatureTypes.FEA_WORKSPACE_MAIN];
        return workspaceMain?.active ? ['/workspaces'] : ['/workspaces', 'projects'];
      }),
      catchError(() => of(['/workspaces', 'projects']))
    );
  }
}
